import { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { listWorldBlock, listWorldObjects } from '../services/worldObjects';

// StreetStream — the city loads what you are near, not what exists.
//
// Until now every street consumer fetched its own kind for the whole world:
// PlacedBuildings asked for every building, NpcManager for every resident, and
// each did it once at mount and kept it forever. That is fine for the town we
// have and impossible for the one the spec describes — "infinite suburbs, one
// downtown" means the number of buildings in the world is unbounded, while the
// number a client can see never changes.
//
// So this is the ONE loader for the streamed kinds, and it asks by CELL:
// Planet v0.2 §10's descent, ORBIT → DISTRICT → STREET → INTERIOR, at the street
// level. Consumers keep reading the store exactly as they did; they simply stop
// deciding what goes in it.
//
// ── WHICH KINDS STREAM, AND WHY NOT ALL OF THEM ─────────────────────────────
//
// The growth law (World Building spec §6b): ANCHORS ARE SHARED, FABRIC IS
// PERSONAL. Anchors are few, structural, and needed whether or not you are
// standing next to them — a venue anchor positions its shell, feeds the map, and
// is the destination of a fast-travel from the other side of the city. Streaming
// five rows saves nothing and breaks all three.
//
// Fabric is the unbounded part: buildings and street residents. Those stream.
const STREAM_KINDS = ['building', 'resident'];

// One district for now. When `world_districts` lands this comes from the
// player's position rather than a constant, which is the only line that changes.
const DISTRICT = 'hypetown';

const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
// ?stream=0 falls back to loading the whole street, which is what every consumer
// did before this file existed. Kept as an escape hatch because "things are
// missing from the city" and "streaming is misbehaving" look identical from
// inside the world, and being able to A/B it in one reload settles which.
const STREAM_ON = qs.get('stream') !== '0';
const RADIUS = Math.min(Math.max(parseInt(qs.get('cellr'), 10) || 3, 0), 8);

export default function StreetStream() {
  const load = useWorldStore((s) => s.loadWorldObjectSlice);
  // The plan view is the MAP — you are looking down at the whole city to decide
  // where things go, so streaming would hide exactly what you opened it to see
  // (and would quietly shorten the admin panels' lists, which read the store).
  // Looking at everything means loading everything.
  const planView = useWorldStore((s) => s.planView);
  const interior = useWorldStore((s) => s.interior);

  const cellSize = useRef(32);   // learned from the API, never assumed
  const atKey = useRef(null);    // centre cell we last loaded around
  const sig = useRef(null);      // signature of what we last published
  const acc = useRef(0);

  const publish = (objects) => {
    for (const kind of STREAM_KINDS) {
      load('street', kind, objects.filter((o) => o.kind === kind));
    }
  };

  // WHOLE-STREET MODE — plan view, or the ?stream=0 escape hatch. One request
  // with no kind filter, rather than one per kind, because the old code's two
  // fetches were two round trips for one answer.
  useEffect(() => {
    if (STREAM_ON && !planView) return;
    let dead = false;
    listWorldObjects('street').then((rows) => {
      if (dead) return;
      atKey.current = null; sig.current = null; // force a re-stream on the way back
      publish(rows);
    });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planView, load]);

  useFrame((state, dt) => {
    if (!STREAM_ON || planView) return;
    // Inside a room the street is not being rendered, and the camera is in room
    // space — reading it here would compute a cell from coordinates that mean
    // nothing on the map and thrash the loader against a position you are not at.
    if (interior) return;

    acc.current += dt;
    if (acc.current < 0.25) return;   // 4Hz; a cell is ~70m, nobody crosses one in 250ms
    acc.current = 0;

    const p = state.camera.position;
    const cx = Math.floor(p.x / cellSize.current);
    const cz = Math.floor(p.z / cellSize.current);
    const key = `${cx}:${cz}`;
    if (key === atKey.current) return;
    atKey.current = key;

    listWorldBlock(DISTRICT, cx, cz, RADIUS).then((r) => {
      cellSize.current = r.cellSize || cellSize.current;

      // SKIP ONLY WHEN THE ANSWER IS GENUINELY THE SAME.
      //
      // Crossing a cell boundary usually returns the same objects — the block
      // shifts by one row of cells and the fringe is empty — and re-publishing
      // identical contents re-renders every consumer for nothing. Worth
      // skipping, but only on a signature that cannot lie.
      //
      // `epoch` ALONE CANNOT DECIDE THIS. It is count:maxUpdatedAt computed per
      // RESPONSE, and what is being compared here are two DIFFERENT blocks, so
      // two blocks holding the same number of rows with the same timestamp
      // collide. That is not contrived: BlockGrid pastes buildings in batches
      // and every row in a batch shares an updated_at. The symptom would be the
      // worst kind — walk into a new block, its buildings never appear, because
      // we decided the answer had not changed.
      //
      // So the signature is BOTH: the ids catch a different set of objects, the
      // epoch catches the same set edited in place.
      const next = `${r.epoch}|${r.objects.map((o) => o.object_id).join(',')}`;
      if (next === sig.current) return;
      sig.current = next;
      publish(r.objects);
    });
  });

  return null;
}
